import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CircularProgress } from "@mui/material"
import { isEmpty } from "lodash"

import Tooltip from "@/components/tooltip/Tooltip"
import { createDrawerStore } from "@/stores/global"
import {
  ChartMultiple,
  ChartMultipleItem,
  SessionDetailsData,
  ToolTipContents,
} from "@/app/report/common/constructor"
import {
  REPORT_SECTION_PADDING,
  REPORT_PADDING,
  DRAWER_WIDTH,
  ACTIVITY_SESSION_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import { createChart, ChartStyle } from "@/app/report/common/chart/multiple"
import { getDateTimeString, getMetricValueStr } from "@/app/report/common/tools"
import { colors } from "@/components/colors"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { ExportFileFunctionType } from "@/app/report/common/type"
import { PDF } from "@/app/report/common/export/pdf"
import {
  CanvasAndFileName,
  exportMultipleChart,
  exportPartialChartWithLegendToSplitted,
} from "@/app/report/common/export/export"

const DEFAULT_CHART_WIDTH = 1200
const DEFAULT_CHART_HEIGHT = 400
const CHART_RATIO = 0.3

function IntervalChart(
  {
    loading,
    chartData,
  }: {
    loading: boolean
    chartData?: SessionDetailsData
  },
  ref: React.ForwardedRef<{
    drawChart: () => void
    exportFile: (args: ExportFileFunctionType) => Promise<void>
  }>,
) {
  const { t } = useTranslation()

  const elementLegendAndChart = useRef<HTMLDivElement>(null)
  const elementLegends = useRef<HTMLDivElement[]>(null)
  const elementChartContainer = useRef<HTMLDivElement>(null)
  const chartYAxisLeft = useRef<HTMLDivElement>(null)
  const chartElements = useRef<HTMLDivElement>(null)
  const chartYAxisRight = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState<number>(0)
  const [chartHeight, setChartHeight] = useState<number>(0)

  const [tooltipTitles, setTooltipTitles] = useState<string[]>([])
  const [tooltipValues, setTooltipValues] = useState<Array<ToolTipContents>>([])
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)
  const svgYAxisLeft = useRef<SVGSVGElement>(null)
  const svgYAxisRight = useRef<SVGSVGElement>(null)
  const elementHighlighted = useRef<HTMLDivElement>(null)

  const { isOpen: isDrawerOpen, isMobile } = createDrawerStore()
  const isMounted = useRef(false)

  useEffect(() => {
    elementLegends.current = []
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  // 초기 마운트 시 resize 실행
  useEffect(() => {
    handleResize()
  }, [])

  // drawer 상태 변경 시 resize (데스크톱만)
  useEffect(() => {
    window.addEventListener("resize", handleResize)

    const isMobileScreen = window.innerWidth < 1200
    if (!isMobileScreen) {
      handleResize()
    }

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (chartData) {
      drawChart()
    }
  }, [chartData, chartWidth, chartHeight])

  const translateKey = useTranslateMetricKey()

  const handleResize = () => {
    const containerWidth = document.querySelector(".Container")?.clientWidth
    if (containerWidth) {
      // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
      // store 타이밍 이슈로 window.innerWidth로 직접 체크
      const isMobileScreen = window.innerWidth < 1200
      const drawerOffset = isDrawerOpen && !isMobileScreen ? DRAWER_WIDTH : 0
      const currentChartWidth =
        containerWidth - drawerOffset - REPORT_PADDING - REPORT_SECTION_PADDING - 20

      if (isMounted.current) {
        setChartWidth(currentChartWidth)
        setChartHeight(currentChartWidth * CHART_RATIO)
      }
    }
  }

  function mouseover(
    e: MouseEvent,
    index: number,
    indexChart: number,
    chartName: string,
    chartMultiple: ChartMultiple,
  ) {
    if (!chartData) {
      return
    }

    switch (chartData.activitySessionType) {
      case ACTIVITY_SESSION_TYPE.ACTIVITY:
        {
          setTooltipTitles([`${index * chartData.interval}’`])
        }
        break

      case ACTIVITY_SESSION_TYPE.SESSION:
        {
          if (chartMultiple.charts.length > 1) {
            setTooltipTitles([
              `${chartMultiple.charts[indexChart].nickname} - ${chartName}`,
              `${index * chartData.interval}’`,
            ])
          } else {
            setTooltipTitles([`${chartName}`, `${index * chartData.interval}’`])
          }
        }
        break
    }

    const newTooltipValues = new Array<ToolTipContents>()
    chartData.axisY.forEach((y, indexColumn) => {
      const value = chartMultiple.charts[indexChart].rows[index].data[indexColumn]
      if (!Number.isNaN(value)) {
        const { unit, key, decimalPoint } = y
        const { calcType, decimalPlace } = chartData.settings || {}
        const valueText = getMetricValueStr({
          value: value as number,
          unit,
          decimalPoint: decimalPlace ?? decimalPoint ?? 1,
          calcType,
          metric: key,
        })
        const newTitle = translateKey(y.key)
        newTooltipValues.push(
          new ToolTipContents({
            title: newTitle || t("metric." + y.key),
            value: valueText,
            color: y.color,
          }),
        )
      }
    })
    setTooltipValues(newTooltipValues)

    elementHighlighted.current = e.target as HTMLDivElement
    if (elementHighlighted.current) {
      elementHighlighted.current.style.fill = "red"
      elementHighlighted.current.style.fillOpacity = "0.1"
      elementHighlighted.current.style.stroke = "red"
      elementHighlighted.current.style.strokeWidth = "1"
      elementHighlighted.current.style.strokeOpacity = "0.4"
    }
  }

  function mousemove() {
    if (!chartData) {
      return
    }
    setTooltipVisible(true)
  }

  function mouseout() {
    if (!chartData) {
      return
    }

    setTooltipVisible(false)

    if (elementHighlighted.current) {
      elementHighlighted.current.style.fillOpacity = "0"
      elementHighlighted.current.style.strokeOpacity = "0"
    }
  }

  const drawChart = useCallback(() => {
    if (!chartData) {
      return
    }

    if (
      !elementChartContainer.current ||
      !chartYAxisLeft.current ||
      !chartElements.current ||
      !chartYAxisRight.current
    ) {
      return
    }

    const charts = chartData.dataInterval.map(
      (chart) => new ChartMultipleItem(chart.name, chart.nickname, chart.data),
    )
    const chartMultiple = new ChartMultiple(chartData.axisY, charts)

    const baseWidth = chartWidth || DEFAULT_CHART_WIDTH
    const chartStyleWidth = chartMultiple.charts.length > 1 ? baseWidth : baseWidth

    const chartStyle = new ChartStyle({
      width: chartStyleWidth,
      height: chartHeight || DEFAULT_CHART_HEIGHT,
    })

    if (chartMultiple.charts.length === 1) {
      chartStyle.space.xAxisChartNameWidth = 0
    }

    const result = createChart({
      chartMultiple,
      style: chartStyle,
      mouseover: (e: MouseEvent, index: number, indexChart: number, chartName: string) =>
        mouseover(e, index, indexChart, chartName, chartMultiple),
      mousemove,
      mouseout,
      translateKey,
    })

    if (
      result &&
      elementChartContainer.current &&
      chartYAxisLeft.current &&
      chartElements.current &&
      chartYAxisRight.current
    ) {
      chartElements.current.innerHTML = ""
      chartYAxisLeft.current.innerHTML = ""
      chartYAxisRight.current.innerHTML = ""

      if (result.svgYAxisLeft) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisLeft.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisLeft.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisLeft)
        chartYAxisLeft.current.appendChild(nodeDiv)
        svgYAxisLeft.current = result.svgYAxisLeft
      }
      if (result.svgYAxisRight) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisRight.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisRight.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisRight)
        chartYAxisRight.current.appendChild(nodeDiv)
        svgYAxisRight.current = result.svgYAxisRight
      }

      const svgCharts = []

      result.svgCharts.forEach((svgChart, index) => {
        if (svgChart) {
          const chartWidth = svgChart.viewBox.baseVal.width
          const chartHeight = svgChart.viewBox.baseVal.height

          const nodeDiv = document.createElement("div")

          nodeDiv.style.width = `${chartWidth}px`
          nodeDiv.style.height = `${chartHeight}px`
          nodeDiv.style.flexShrink = "0"
          nodeDiv.style.margin = "0"
          nodeDiv.style.padding = "0"

          svgChart.setAttribute("width", `${chartWidth}`)
          svgChart.setAttribute("height", `${chartHeight}`)

          nodeDiv.appendChild(svgChart)
          chartElements.current?.appendChild(nodeDiv)
          svgCharts.push(svgChart)
        }
      })

      if (chartElements.current && result.svgCharts.length > 1) {
        chartElements.current.style.display = "flex"
        chartElements.current.style.overflowX = "auto"
        chartElements.current.style.overflowY = "hidden"
      }
    }
  }, [chartData, chartWidth, chartHeight])

  const exportFile = async (args: ExportFileFunctionType) => {
    const { exportType, magnification, reportType, chartAnnotation, decimalPoints } = args
    const pdf = await PDF.create()
    const FILE_NAME_HEADER = `${reportType}_report_chart_interval`

    if (!chartData || !elementLegendAndChart.current || !elementLegends.current) {
      return
    }

    const charts = chartData.dataInterval.map(
      (chart) => new ChartMultipleItem(chart.name, chart.nickname, chart.data),
    )
    const chartMultiple = new ChartMultiple(chartData.axisY, charts)

    const legends = elementLegends.current.map((elementLegendItem, index) => {
      const node = elementLegendItem as HTMLDivElement
      const left = node.offsetLeft - (elementLegendAndChart.current?.offsetLeft || 0)

      return {
        text: translateKey(chartMultiple.axisY[index].key),
        color: chartMultiple.axisY[index].color,
        left: left,
      }
    })

    const chartSizeForExport = { width: 1200, height: 400 }
    const chartStyle = new ChartStyle({
      width: chartSizeForExport.width,
      height: chartSizeForExport.height,
      magnification,
    })
    chartStyle.valuePositionType = chartAnnotation as VALUE_POSITION_TYPE

    if (chartData.activitySessionType === ACTIVITY_SESSION_TYPE.ACTIVITY) {
      chartStyle.space.xAxisChartNameWidth = 3
      chartStyle.flagShowChartLabelOnXAxis = false
    }

    const result = createChart({
      chartMultiple,
      style: chartStyle,
      mouseover: (e: MouseEvent, index: number, indexChart: number, chartName: string) =>
        mouseover(e, index, indexChart, chartName, chartMultiple),
      mousemove,
      mouseout,
      translateKey,
      decimalPointForExportFile: decimalPoints,
    })

    const widthYAxisLeft = result?.svgYAxisLeft?.viewBox.baseVal.width || 0
    const widthYAxisRight = result?.svgYAxisRight ? result?.svgYAxisRight.viewBox.baseVal.width : 0
    const widthSpaceForChartUsable =
      chartStyle.size.width -
      widthYAxisLeft -
      widthYAxisRight -
      chartStyle.space.xAxisChartNameWidth
    const countOfXAxisItem = Math.floor(widthSpaceForChartUsable / chartStyle.widthOfXAxisItem)

    const promises = result?.svgCharts.map((svgChart, index) => {
      let fileName = `${getDateTimeString()}_${FILE_NAME_HEADER}_${index}`
      if (!isEmpty(chartData.dataInterval[index].name)) {
        fileName += `_${chartData.dataInterval[index].name}`
      }
      if (result && result.svgYAxisLeft && svgChart) {
        return exportPartialChartWithLegendToSplitted(
          svgChart,
          result.svgYAxisLeft,
          result?.svgYAxisRight,
          legends,
          fileName,
          chartStyle,
          "white",
          exportType,
          true,
          pdf,
          countOfXAxisItem,
          true,
        )
      } else {
        return null
      }
    })

    if (promises !== undefined) {
      Promise.all(promises).then((results: Array<CanvasAndFileName[] | null>) => {
        exportMultipleChart(exportType, results, FILE_NAME_HEADER, pdf)
      })
    }
  }

  useImperativeHandle(ref, () => ({
    drawChart,
    exportFile: async (props: ExportFileFunctionType) => await exportFile(props),
  }))

  if (loading) {
    return (
      <div className="MuiLoadingSpinnerCont">
        <CircularProgress size={48} />
      </div>
    )
  }

  return (
    <div className="IntervalChartCont">
      <Tooltip
        type="chart"
        label={
          tooltipTitles.length > 1
            ? `${tooltipTitles[0]}
${tooltipTitles[1]}`
            : tooltipTitles[0]
        }
        title={
          <div className="bar-chart-tooltip">
            {tooltipValues.map((value, index) => {
              if (!value.value || !value.title) {
                return null
              }

              return (
                <div key={index} className="bar-chart-tooltip-item">
                  <span
                    style={{
                      backgroundColor: value.color || colors.neutral.black,
                      color: value.color || colors.neutral.black,
                    }}
                  />
                  <p>{value.title}:</p>
                  <p>{value.value}</p>
                </div>
              )
            })}
          </div>
        }
        size="medium"
        open={tooltipVisible}>
        <div className="chart">
          <div ref={elementLegendAndChart} className="element-legend-and-chart">
            <div className="legend">
              {chartData?.axisY.map((axisY, index) => {
                const newAxisKey = translateKey(axisY.key)

                return (
                  <div key={index}>
                    <div
                      ref={(el) => {
                        if (el && elementLegends.current) {
                          elementLegends.current[index] = el
                        }
                      }}
                      className="element-legend">
                      <span
                        style={{
                          color: axisY.color,
                        }}>
                        ●
                      </span>
                      <span className="legend-text">{newAxisKey}</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div ref={elementChartContainer} className="element-chart-container">
              <table>
                <tbody>
                  <tr>
                    <td className="chart-y-axis-left">
                      <div ref={chartYAxisLeft}></div>
                    </td>
                    <td>
                      <div ref={chartElements} className="chart-elements"></div>
                    </td>
                    <td className="chart-y-axis-right">
                      <div ref={chartYAxisRight}></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Tooltip>
    </div>
  )
}

export default forwardRef(IntervalChart)
